import React from 'react';
import {
    selectAvailableLanguages,
    selectPreferredLanguage
} from 'tradera/state/language/selectors';
import { setPreferredLanguage } from 'tradera/state/language/actions';
import { useTranslator, useUrlLocalizer } from 'tradera/lang/translate';
import { SURVEYS } from 'tradera/constants/surveys';
import Flag from 'tradera/components/flag/flag';
import { getCountryCodeForFlagFromLanguage } from 'tradera/localization/languagecode';
import { useQualtive } from 'tradera/hooks/use-qualtive';
import CheckIcon from '@tradera/blueprint/theme/icons2/check.svg?jsx';
import styles from './language-select.module.scss';
import { TranslationPreferredMenuItem } from './translation-preferred-menu-item';
import { Button } from 'tradera/components/atoms/button/button';
import { useAppDispatch, useAppSelector } from 'tradera/state/hooks';

type PlainLanguageSelectProps = {
    inProfileMenu?: boolean;
    title?: string;
};

export const LanguageSelect = ({
    inProfileMenu,
    title
}: PlainLanguageSelectProps) => {
    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();
    const { isQualtiveEnabled, openQualtiveModal } = useQualtive();
    const dispatch = useAppDispatch();
    const availableLanguages = useAppSelector(selectAvailableLanguages);
    const preferredLanguage = useAppSelector(selectPreferredLanguage);

    const onLanguageChanged = (code: string) => {
        if (preferredLanguage?.languageCodeIso2 === code) {
            return;
        }
        dispatch(setPreferredLanguage(code, toLocalizedUrl));
    };
    return (
        <div className={styles.languageSelect}>
            {title && (
                <div className="ml-2 py-1">
                    <strong>{title}</strong>
                </div>
            )}
            {availableLanguages?.map(({ languageCodeIso2, displayName }) => (
                <Button
                    fluid
                    theme="mainMenu"
                    className={`${
                        styles.languageSelectRow
                    } d-flex align-items-center py-1 px-2 ${
                        inProfileMenu ? 'pl-5' : ''
                    }`}
                    key={languageCodeIso2}
                    onClick={() => {
                        onLanguageChanged(languageCodeIso2 || '');
                    }}
                    data-language-select={languageCodeIso2}>
                    <span
                        className={`d-flex align-items-center ${
                            inProfileMenu ? 'pl-2' : ''
                        }`}>
                        <Flag
                            countryCodeIso2={getCountryCodeForFlagFromLanguage(
                                languageCodeIso2
                            )}
                            className="mr-1"
                        />
                        {preferredLanguage?.languageCodeIso2 ===
                        languageCodeIso2 ? (
                            <strong>{displayName}</strong>
                        ) : (
                            <span>{displayName}</span>
                        )}
                    </span>
                    {preferredLanguage?.languageCodeIso2 ===
                        languageCodeIso2 && (
                        <CheckIcon className="icon ml-auto" />
                    )}
                </Button>
            ))}
            <TranslationPreferredMenuItem />
            {isQualtiveEnabled && (
                <div className="p-2 text-inter-light size-oslo">
                    {t('siteHeader_languageDisclaimerShort')}&nbsp;
                    <Button
                        theme="link"
                        onClick={() => openQualtiveModal(SURVEYS.language)}>
                        {t('siteHeader_languageLeaveFeedBack')}
                    </Button>
                </div>
            )}
        </div>
    );
};
